/**
 * Product / Variant / Category — mirrors `backend/prisma/schema.prisma`.
 * Money fields are in paise (integer) unless explicitly noted.
 */

export type GstRate = 'RATE_5' | 'RATE_12';
export type Gender = 'MALE' | 'FEMALE' | 'UNISEX' | 'KIDS';
export type Size = 'XS' | 'S' | 'M' | 'L' | 'XL' | 'XXL' | 'XXXL' | 'FREE_SIZE';
export type VariantStatus = 'ACTIVE' | 'OUT_OF_STOCK' | 'DISCONTINUED';

export interface Category {
  id: number;
  name: string;
  slug: string;
  parentId: number | null;
  description: string | null;
  imageUrl: string | null;
  isActive: boolean;
  sortOrder: number;
  children?: Category[];
}

export interface ProductImage {
  id: number;
  productId: number;
  cloudinaryId: string;
  url: string;
  altText: string | null;
  sortOrder: number;
  isPrimary: boolean;
}

export interface ProductVariant {
  id: number;
  productId: number;
  sku: string;
  size: Size;
  color: string;
  colorHex: string | null;
  /** Per-colour product photo (CDN URL); drives the colour swatch + gallery. */
  imageUrl: string | null;
  stock: number;
  status: VariantStatus;
  weight: number | null;
  /** Premium added on top of basePrice for this variant (paise). */
  additionalPrice: number;
}

export interface Product {
  id: number;
  slug: string;
  name: string;
  description: string;
  brand: string | null;
  categoryId: number;
  /** paise */
  basePrice: number;
  /** paise — null when no discount */
  discountedPrice: number | null;
  gstRate: GstRate;
  gender: Gender;
  material: string | null;
  fabric: string | null;
  careInstructions: string | null;
  tags: string | null;
  isActive: boolean;
  isFeatured: boolean;
  averageRating: number;
  reviewCount: number;
  createdAt: string;
  updatedAt: string;

  category?: Category;
  variants?: ProductVariant[];
  images?: ProductImage[];
}

/** Numeric value (5/12) extracted from the GstRate enum. */
export const GST_RATE_NUMERIC: Record<GstRate, number> = {
  RATE_5: 0.05,
  RATE_12: 0.12,
};

/** Compute the "effective" price for a product+variant in paise. */
export function effectivePricePaise(
  product: Pick<Product, 'basePrice' | 'discountedPrice'>,
  variant?: Pick<ProductVariant, 'additionalPrice'>
): number {
  const base = product.discountedPrice ?? product.basePrice;
  return base + (variant?.additionalPrice ?? 0);
}
